import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, ArrowTabStepper, Button, Card, useToast } from '@kss/ui';
import { apiClient } from '@kss/api';
import {
  ArrowLeft, Check, KeyRound, ShieldAlert, ShieldCheck, Smartphone,
} from 'lucide-react';

/**
 * MFA enrolment / disable page.
 *
 * Flows:
 *   - GET  /api/auth/mfa/status        — am I already enrolled?
 *   - POST /api/auth/mfa/enroll        — kick off enrolment (returns
 *                                        secret + otpauth:// URI for the
 *                                        authenticator)
 *   - POST /api/auth/mfa/verify-enroll — finalise (user submits the
 *                                        6-digit TOTP they see in the app)
 *   - POST /api/auth/mfa/disable       — turn it off (with current TOTP)
 *
 * If the otpauth URI comes back from the API we render a QR image via
 * the public qrserver.com renderer. Cashiers without internet can still
 * paste the URI directly into their authenticator app.
 */

interface MfaStatus {
  /** Live backend field. */
  isTwoFactorEnabled?: boolean;
  isEnrollmentPending?: boolean;
  enabled?: boolean;
  isEnabled?: boolean;
  type?: 'TOTP' | 'SMS' | string;
}

/** Pull the server's error message out of an axios error, falling back. */
function serverErr(e: unknown, fallback: string): string {
  const resp = (e as { response?: { data?: { error?: string; message?: string; title?: string } } })?.response;
  return resp?.data?.error ?? resp?.data?.message ?? resp?.data?.title ?? (e instanceof Error ? e.message : fallback);
}

interface EnrollResponse {
  secret?: string;
  otpAuthUri?: string;
  otpauthUrl?: string;
  qrCode?: string;
}

const STEPS = [
  { key: 'begin',  label: 'Begin'   },
  { key: 'scan',   label: 'Scan QR' },
  { key: 'verify', label: 'Verify'  },
  { key: 'done',   label: 'Done'    },
];

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const otpCls =
  'h-12 w-full rounded-md border border-border bg-surface-card px-3 text-center text-2xl font-semibold tracking-[0.4em] text-content-primary outline-none focus:border-primary';

export default function MfaPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [step, setStep] = useState(0);
  const [savedIndices, setSavedIndices] = useState<number[]>([]);
  const [secret, setSecret] = useState('');
  const [otpUri, setOtpUri] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [password, setPassword] = useState('');
  const [qrFailed, setQrFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Load status on mount.
  useEffect(() => {
    setLoading(true);
    apiClient
      .get('/auth/mfa/status')
      .then((r) => {
        const d = (r.data?.data ?? r.data) as MfaStatus;
        // Live backend returns isTwoFactorEnabled (not enabled/isEnabled).
        setEnabled(!!(d?.isTwoFactorEnabled ?? d?.enabled ?? d?.isEnabled));
      })
      .catch((e) => setErr(serverErr(e, 'Could not load MFA status')))
      .finally(() => setLoading(false));
  }, []);

  async function beginEnroll() {
    setBusy(true);
    setErr(null);
    setQrFailed(false);
    try {
      const r = await apiClient.post('/auth/mfa/enroll', {});
      const d = (r.data?.data ?? r.data) as EnrollResponse;
      const uri = String(d?.otpAuthUri ?? d?.otpauthUrl ?? '');
      setSecret(String(d?.secret ?? ''));
      setOtpUri(uri);
      if (!uri) {
        // No QR came back — don't strand the user on a blank step 1.
        setErr('Enrolment did not return a QR. Please retry.');
        return;
      }
      setSavedIndices((p) => (p.includes(0) ? p : [...p, 0]));
      setStep(1);
    } catch (e) {
      const msg = serverErr(e, 'Enrolment failed');
      // If MFA is already enabled on this account, switch to the disable view
      // instead of leaving the user stuck on step 0.
      if (/already\s*(enabled|enrolled)/i.test(msg)) {
        setEnabled(true);
        setErr(null);
      } else {
        setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (otpCode.length !== 6) {
      setErr('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await apiClient.post('/auth/mfa/verify-enroll', { code: otpCode, otpCode });
      toast.success('MFA enabled', 'Your account now requires a code at sign-in.');
      setSavedIndices((p) => Array.from(new Set([...p, 1, 2])));
      setEnabled(true);
      setStep(3);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Code rejected — try again.');
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (otpCode.length !== 6) {
      setErr('Enter your current 6-digit code to confirm.');
      return;
    }
    if (!password.trim()) {
      setErr('Enter your account password to confirm.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      // Backend requires BOTH password and code.
      await apiClient.post('/auth/mfa/disable', { password, code: otpCode });
      toast.success('MFA disabled', 'Your account no longer requires a second factor.');
      setEnabled(false);
      setOtpCode('');
      setPassword('');
    } catch (e) {
      setErr(serverErr(e, 'Could not disable MFA.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <Button variant="outline" size="sm" onClick={() => navigate('/panel/profile')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Multi-factor authentication</h1>
          <p className="text-xs text-content-muted">
            {enabled
              ? 'MFA is active — sign-ins require a 6-digit code from your authenticator.'
              : 'Add a second factor to your account using any TOTP app (Authy, Google Authenticator, 1Password, …).'}
          </p>
        </div>
        {enabled ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-success/40 bg-success/10 px-3 py-1 text-xs text-success">
            <ShieldCheck size={14} /> Enabled
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-warning/40 bg-warning/10 px-3 py-1 text-xs text-warning">
            <ShieldAlert size={14} /> Not enabled
          </span>
        )}
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      {loading ? (
        <Card>
          <div className="p-6 text-sm text-content-muted">Loading status…</div>
        </Card>
      ) : enabled ? (
        <Card>
          <h3 className="mb-3 text-sm font-semibold text-content-primary">Disable MFA</h3>
          <p className="mb-3 text-xs text-content-secondary">
            Enter your current 6-digit code to confirm. Disabling MFA reduces account
            security — proceed only if you&rsquo;ve lost access to your authenticator and need
            to re-enrol.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">
                Current code <span className="text-danger">*</span>
              </span>
              <input
                inputMode="numeric"
                maxLength={6}
                className={otpCls}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="••••••"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-content-secondary">
                Account password <span className="text-danger">*</span>
              </span>
              <input
                type="password"
                className={inputCls}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your login password"
                autoComplete="current-password"
              />
            </label>
            <div className="flex items-end">
              <Button onClick={disable} disabled={busy || otpCode.length !== 6 || !password.trim()}>
                <ShieldAlert className="h-4 w-4" /> {busy ? 'Disabling…' : 'Disable MFA'}
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <Card>
          <ArrowTabStepper
            compact
            steps={STEPS.map((s) => ({ key: s.key, label: s.label }))}
            activeIndex={step}
            savedIndices={savedIndices}
            onChange={(i) => setStep(i)}
          />

          <div className="mt-4">
            {step === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-content-secondary">
                  You&rsquo;ll need a TOTP authenticator app on your phone — Google Authenticator,
                  Authy, 1Password, Microsoft Authenticator, etc. Click <em>Begin</em> when ready.
                </p>
                <ul className="grid gap-2 text-xs text-content-muted sm:grid-cols-3">
                  <li className="rounded-md border border-border bg-surface-card px-3 py-2">
                    1. Install your authenticator app
                  </li>
                  <li className="rounded-md border border-border bg-surface-card px-3 py-2">
                    2. Scan the QR we&rsquo;ll show you
                  </li>
                  <li className="rounded-md border border-border bg-surface-card px-3 py-2">
                    3. Enter the 6-digit code to verify
                  </li>
                </ul>
              </div>
            )}

            {step === 1 && (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex flex-col items-center justify-center rounded-md border border-border bg-surface-card p-4">
                  {otpUri && !qrFailed ? (
                    <img
                      alt="MFA QR code"
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpUri)}`}
                      width={200}
                      height={200}
                      className="rounded-md bg-white p-2"
                      onError={() => setQrFailed(true)}
                    />
                  ) : otpUri && qrFailed ? (
                    <div className="grid h-[200px] w-[200px] place-items-center rounded-md border border-dashed border-warning p-3 text-center text-xs text-content-muted">
                      QR renderer unreachable. Use the <strong>manual key</strong> below in your authenticator app.
                    </div>
                  ) : (
                    <div className="grid h-[200px] w-[200px] place-items-center rounded-md border border-dashed border-border text-xs text-content-muted">
                      Click <em>Begin</em> first
                    </div>
                  )}
                  {secret && (
                    <div className="mt-3 w-full">
                      <div className="text-[10px] uppercase tracking-wider text-content-muted">Manual key</div>
                      <code className="mt-1 block break-all rounded-md border border-border bg-surface-card-hover px-2 py-1.5 font-mono text-xs">
                        {secret}
                      </code>
                    </div>
                  )}
                </div>
                <div className="space-y-2 text-sm text-content-secondary">
                  <p>
                    <Smartphone className="mr-1 inline h-4 w-4" /> Open your authenticator app and
                    add a new account by scanning the QR.
                  </p>
                  <p>
                    Can&rsquo;t scan? Tap your app&rsquo;s "Enter setup key" option and paste the manual
                    key above. The account name will appear as <code>KSS BillBook</code>.
                  </p>
                  <p className="text-xs text-content-muted">
                    Once the app shows a 6-digit rolling code, click <em>Next</em>.
                  </p>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">
                    Code from authenticator <span className="text-danger">*</span>
                  </span>
                  <input
                    inputMode="numeric"
                    maxLength={6}
                    className={otpCls}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="••••••"
                    autoFocus
                  />
                  <span className="text-[11px] text-content-muted">
                    Codes refresh every 30 seconds — if your code expires before you submit, just
                    grab the next one.
                  </span>
                </label>
              </div>
            )}

            {step === 3 && (
              <Alert variant="success">
                ✓ MFA is now enabled. Next time you sign in you&rsquo;ll be prompted for the 6-digit
                code from your authenticator.
              </Alert>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
            <div className="text-xs text-content-muted">
              Step {step + 1} of {STEPS.length} — {STEPS[step]?.label}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => navigate('/panel/profile')} disabled={busy}>
                Cancel
              </Button>
              {step === 0 && (
                <Button onClick={beginEnroll} disabled={busy}>
                  <KeyRound className="h-4 w-4" /> {busy ? 'Starting…' : 'Begin'}
                </Button>
              )}
              {step === 1 && (
                <Button onClick={() => { setSavedIndices((p) => [...p, 1]); setStep(2); }} disabled={busy || !otpUri}>
                  Next
                </Button>
              )}
              {step === 2 && (
                <Button onClick={verify} disabled={busy || otpCode.length !== 6}>
                  <Check className="h-4 w-4" /> {busy ? 'Verifying…' : 'Verify & enable'}
                </Button>
              )}
              {step === 3 && (
                <Button onClick={() => navigate('/panel/profile')}>Done</Button>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
